import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button
} from '@mui/material';
import {
  Camera,
  History,
  Home,
  Agriculture
} from '@mui/icons-material';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import FarmRoadService from './FarmRoadService';
import FarmRoadImageService from './FarmRoadImageService';
import ImageUploadZone from './ImageUploadZone';
import ImageAnalysisResults from './ImageAnalysisResults';

const FarmRoadImageAnalysis = () => {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();

  // Tab state
  const [currentTab, setCurrentTab] = useState(0);

  // Farm data
  const [farms, setFarms] = useState([]);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [farmDetail, setFarmDetail] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [compartments, setCompartments] = useState([]);

  // Image analysis data
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [selectedHistoryAnalysis, setSelectedHistoryAnalysis] = useState(null); // Pour afficher une analyse spécifique dans l'historique
  const [isUploadingNew, setIsUploadingNew] = useState(false);

  // Loading states
  const [loadingFarms, setLoadingFarms] = useState(true);
  const [loadingFarmDetails, setLoadingFarmDetails] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Error state
  const [error, setError] = useState(null);

  const userId = user?.uid;

  /**
   * Load farms on mount
   */
  useEffect(() => {
    if (!userId) {
      navigate('/');
      return;
    }

    loadFarms();
  }, [userId]);

  /**
   * Load farm details when farm is selected
   */
  useEffect(() => {
    if (selectedFarm) {
      loadFarmDetails();
      loadAnalysisHistory();
    }
  }, [selectedFarm]);

  /**
   * Load accessible farms
   */
  const loadFarms = async () => {
    try {
      setLoadingFarms(true);
      setError(null);

      const farmsData = await FarmRoadService.getFarms();
      setFarms(farmsData);

      // Auto-select first farm if available
      if (farmsData.length > 0 && !selectedFarm) {
        setSelectedFarm(farmsData[0]);
      }
    } catch (err) {
      console.error('Error loading farms:', err);
      setError('Erreur lors du chargement des fermes: ' + err.message);
    } finally {
      setLoadingFarms(false);
    }
  };

  /**
   * Load farm details (sectors, compartments)
   */
  const loadFarmDetails = async () => {
    if (!selectedFarm) return;

    try {
      setLoadingFarmDetails(true);
      setError(null);

      // OPTIMISATION : Load farm structure from PostgreSQL instead of FarmRoad API
      console.log('🔍 Chargement de la structure pour farmId:', selectedFarm.id);

      const [sectorsData, compartmentsData] = await Promise.all([
        FarmRoadService.getSectors(selectedFarm.id),
        FarmRoadService.getCompartments(selectedFarm.id)
      ]);

      console.log('📊 Données reçues:', {
        sectors: sectorsData,
        compartments: compartmentsData,
        sectorsCount: sectorsData?.length || 0,
        compartmentsCount: compartmentsData?.length || 0
      });

      // Reconstruct farmDetail from PostgreSQL data
      const sectors = Array.isArray(sectorsData) ? sectorsData : [];
      const compartments = Array.isArray(compartmentsData) ? compartmentsData : [];

      const compartmentsWithSectors = compartments.map(comp => ({
        ...comp,
        sectors: sectors.filter(sector => sector.compartment_id === comp.id)
      }));

      const farmData = {
        id: selectedFarm.id,
        name: selectedFarm.name,
        location: selectedFarm.location,
        compartments: compartmentsWithSectors
      };

      setFarmDetail(farmData);

      // Helper function to enrich sectors with compartment names
      const enrichSectorsWithCompartmentNames = (sectors, compartments) => {
        return sectors.map(sector => {
          const sectorCompartmentId = sector.compartmentId || sector.compartment_id;
          const compartment = compartments.find(c =>
            (c.compartmentId || c.id) === sectorCompartmentId
          );
          return {
            ...sector,
            compartmentName: compartment?.name || null
          };
        });
      };

      // If sectors/compartments are empty, sync from FarmRoad API first
      if (!sectorsData || sectorsData.length === 0 || !compartmentsData || compartmentsData.length === 0) {
        console.log('⚠️ Secteurs/compartiments vides, synchronisation depuis API FarmRoad...');

        try {
          await FarmRoadService.syncStructure(selectedFarm.id);
          console.log('✅ Structure synchronisée, rechargement...');

          // Reload sectors and compartments after sync
          const [newSectorsData, newCompartmentsData] = await Promise.all([
            FarmRoadService.getSectors(selectedFarm.id),
            FarmRoadService.getCompartments(selectedFarm.id)
          ]);

          console.log('📊 Données après sync:', {
            newSectors: newSectorsData,
            newCompartments: newCompartmentsData,
            newSectorsCount: newSectorsData?.length || 0,
            newCompartmentsCount: newCompartmentsData?.length || 0
          });

          const enrichedSectors = enrichSectorsWithCompartmentNames(
            newSectorsData || [],
            newCompartmentsData || []
          );

          console.log('✅ Secteurs enrichis:', enrichedSectors);

          setSectors(enrichedSectors);
          setCompartments(newCompartmentsData || []);
        } catch (syncErr) {
          console.error('Erreur lors de la synchronisation:', syncErr);
          // Use empty arrays if sync fails
          setSectors([]);
          setCompartments([]);
          setError('Impossible de charger la structure de la ferme. Vérifiez votre clé API FarmRoad.');
        }
      } else {
        const enrichedSectors = enrichSectorsWithCompartmentNames(
          sectorsData || [],
          compartmentsData || []
        );
        setSectors(enrichedSectors);
        setCompartments(compartmentsData || []);
      }

    } catch (err) {
      console.error('Error loading farm details:', err);
      setError('Erreur lors du chargement des détails de la ferme: ' + err.message);
    } finally {
      setLoadingFarmDetails(false);
    }
  };

  /**
   * Load analysis history
   */
  const loadAnalysisHistory = async () => {
    if (!selectedFarm) return;

    try {
      setLoadingHistory(true);

      const historyData = await FarmRoadImageService.getHistory(selectedFarm.id, {
        limit: 50,
        sortBy: 'uploaded_at',
        sortOrder: 'DESC'
      });

      setAnalysisHistory(historyData.images || []);

    } catch (err) {
      console.error('Error loading analysis history:', err);
      // Don't show error for history as it's not critical
    } finally {
      setLoadingHistory(false);
    }
  };

  /**
   * Handle upload complete
   */
  const handleUploadComplete = (uploadResult) => {
    console.log('Upload complete:', uploadResult);
    setCurrentImageUrl(uploadResult.imageUrl);
  };

  /**
   * Avant un nouvel upload: masquer l'ancienne analyse
   */
  const handleBeforeUpload = () => {
    setIsUploadingNew(true);
    setCurrentAnalysis(null);
  };

  /**
   * Handle analysis complete
   */
  const handleAnalysisComplete = (analysisData) => {
    console.log('Analysis complete:', analysisData);
    console.log('📊 Détails de l\'objet analysis reçu:', {
      typeOf: typeof analysisData.analysis,
      keys: analysisData.analysis ? Object.keys(analysisData.analysis) : [],
      healthScore: analysisData.analysis?.healthScore,
      growthStage: analysisData.analysis?.growthStage,
      fullObject: analysisData.analysis
    });
    // Conserver l'ID d'image pour l'export PDF ultérieur
    setCurrentAnalysis({ ...analysisData.analysis, imageId: analysisData.imageId });
    setCurrentImageUrl(analysisData.imageUrl);

    // Refresh history
    loadAnalysisHistory();

    // Fin d'upload
    setIsUploadingNew(false);

    // Switch to results tab
    setCurrentTab(0);
  };

  /**
   * Handle PDF export
   */
  const handleExportPDF = async () => {
    if (!currentAnalysis?.imageId) return;

    try {
      const pdfBlob = await FarmRoadImageService.exportPDF(currentAnalysis.imageId, {
        includeImage: true,
        includeAnnotations: true,
        includeSensorData: true,
        includeRecommendations: true
      });

      // Download PDF
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analyse-framboisier-${currentAnalysis.imageId}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError('Erreur lors de l\'export PDF: ' + err.message);
    }
  };

  /**
   * Handle tab change
   */
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  /**
   * Handle farm selection
   */
  const handleFarmChange = (event) => {
    const farm = farms.find(f => f.id === event.target.value);
    setSelectedFarm(farm);
    setCurrentAnalysis(null);
    setCurrentImageUrl(null);
  };

  /**
   * Convert AI model name to user-friendly label
   */
  const getAnalysisTypeLabel = (aiModel) => {
    if (!aiModel) return 'Analyse Flash';
    const modelLower = aiModel.toLowerCase();
    if (modelLower.includes('pro')) {
      return 'Analyse Advanced';
    }
    return 'Analyse Flash';
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Vous devez être connecté pour accéder à cette page.
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: { xs: 2, sm: 4 } }}>
      <Container maxWidth="xl" sx={{ pt: { xs: 2, sm: 3 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: { xs: 2, sm: 3 } }}>
          <Link
            underline="hover"
            color="inherit"
            href="/farmroad"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <Agriculture fontSize="small" />
            FarmRoad
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Camera fontSize="small" />
            Analyse d'images IA
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Camera fontSize="large" color="primary" />
            Analyse d'images
          </Typography>
          {/* Description - Short version on mobile */}
          <Typography variant="body1" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
            Analyse IA de vos plants : maladies, nuisibles, croissance et recommandations.
          </Typography>
          {/* Description - Full version on desktop */}
          <Typography variant="body1" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Téléchargez des photos de vos plants, feuilles ou fruits pour une analyse IA complète :
            détection de maladies, nuisibles, anomalies de croissance, mesure des fruits et recommandations.
          </Typography>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loadingFarmDetails && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Main Content */}
        {!loadingFarmDetails && selectedFarm && (
          <Paper elevation={2}>
            {/* Tabs */}
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                px: { xs: 1, sm: 2 },
                pt: { xs: 1, sm: 2 },
                '& .MuiTab-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  minWidth: { xs: 90, sm: 120 },
                  px: { xs: 1, sm: 2 }
                }
              }}
            >
              <Tab
                icon={<Camera />}
                label="Nouvelle analyse"
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 500 }}
              />
              <Tab
                icon={<History />}
                label={`Historique (${analysisHistory.length})`}
                iconPosition="start"
                sx={{ textTransform: 'none', fontWeight: 500 }}
              />
            </Tabs>

            {/* Tab Panels */}
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              {/* Tab 0: Upload & Current Analysis */}
              {currentTab === 0 && (
                <Box>
                  <ImageUploadZone
                    farmId={selectedFarm.id}
                    farmName={selectedFarm.name}
                    sectors={sectors}
                    compartments={compartments}
                    culture={farmDetail?.culture || 'framboises'}
                    onUploadComplete={handleUploadComplete}
                    onAnalysisComplete={handleAnalysisComplete}
                    onBeforeUpload={handleBeforeUpload}
                    activeImageUrl={currentImageUrl}
                  />

                  {/* Display current analysis results */}
                  {currentAnalysis && !isUploadingNew && (
                    <Box sx={{ mt: 4 }}>
                      <ImageAnalysisResults
                        analysis={currentAnalysis}
                        imageUrl={currentImageUrl}
                        onExportPDF={handleExportPDF}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {/* Tab 1: History */}
              {currentTab === 1 && (
                <Box>

                  {loadingHistory ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : analysisHistory.length === 0 ? (
                    <Alert severity="info">
                      Aucune analyse disponible pour cette ferme. Commencez par télécharger une image !
                    </Alert>
                  ) : selectedHistoryAnalysis ? (
                    // Vue détaillée d'une analyse sélectionnée
                    <Box>
                      <Button
                        variant="outlined"
                        startIcon={<History />}
                        onClick={() => setSelectedHistoryAnalysis(null)}
                        sx={{ mb: 3 }}
                      >
                        ← Retour à la liste
                      </Button>

                      <Box sx={{ mt: 2 }}>
                        <ImageAnalysisResults
                          analysis={selectedHistoryAnalysis.analysis}
                          imageUrl={selectedHistoryAnalysis.imageUrl}
                          onExportPDF={async () => {
                            const imageId = selectedHistoryAnalysis.analysis?.imageId;
                            if (!imageId) return;
                            try {
                              const pdfBlob = await FarmRoadImageService.exportPDF(imageId, {
                                includeImage: true,
                                includeAnnotations: true,
                                includeSensorData: true,
                                includeRecommendations: true
                              });
                              const url = window.URL.createObjectURL(pdfBlob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `analyse-framboisier-${imageId}-${new Date().toISOString().split('T')[0]}.pdf`;
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                            } catch (err) {
                              console.error('Error exporting PDF:', err);
                              setError('Erreur lors de l\'export PDF: ' + err.message);
                            }
                          }}
                        />
                      </Box>
                    </Box>
                  ) : (
                    // Vue liste des analyses
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(auto-fill, minmax(280px, 1fr))'
                      },
                      gap: { xs: 2, sm: 3 }
                    }}>
                      {analysisHistory.map((analysis) => {
                        // Fonction sécurisée pour parser le JSON
                        const safeJsonParse = (jsonString, defaultValue) => {
                          // Si c'est déjà un objet ou un tableau, le retourner directement
                          if (typeof jsonString === 'object' && jsonString !== null) {
                            return jsonString;
                          }
                          // Si c'est null, undefined, ou une chaîne vide
                          if (!jsonString || jsonString === 'null' ||
                              (typeof jsonString === 'string' && jsonString.trim() === '')) {
                            return defaultValue;
                          }
                          try {
                            return JSON.parse(jsonString);
                          } catch (e) {
                            console.warn('Erreur parsing JSON:', e);
                            return defaultValue;
                          }
                        };

                        const analysisData = {
                          healthScore: parseFloat(analysis.health_score) || 0,
                          growthStage: analysis.growth_stage,
                          overallStatus: { condition: analysis.overall_status },
                          diseasesDetected: safeJsonParse(analysis.diseases_detected, []),
                          pestsDetected: safeJsonParse(analysis.pests_detected, []),
                          growthAnomalies: safeJsonParse(analysis.growth_anomalies, []),
                          fruitMeasurements: safeJsonParse(analysis.fruit_measurements, {}),
                          recommendations: safeJsonParse(analysis.recommendations, []),
                          aiModel: analysis.ai_model,
                          analyzedAt: analysis.analyzed_at,
                          imageId: analysis.image_id
                        };

                        return (
                          <Card
                            key={analysis.image_id || analysis.id}
                            elevation={2}
                            sx={{
                              cursor: 'pointer',
                              transition: 'all 0.3s',
                              '@media (hover: hover)': {
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: 4
                                }
                              },
                              '&:active': {
                                transform: 'scale(0.98)',
                                boxShadow: 2
                              }
                            }}
                            onClick={async () => {
                              console.log('📷 Clic sur carte historique:', {
                                id: analysis.id,
                                sectorName: analysis.sector_name,
                                healthScore: analysisData.healthScore
                              });

                              // Générer une URL signée pour l'image
                              try {
                                console.log('🔐 Génération URL signée pour image ID:', analysis.id);
                                const signedUrl = await FarmRoadImageService.getSignedUrl(analysis.id);
                                console.log('✅ URL signée générée:', signedUrl.substring(0, 100) + '...');

                                setCurrentImageUrl(signedUrl);
                                setSelectedHistoryAnalysis({
                                  analysis: analysisData,
                                  imageUrl: signedUrl,
                                  sectorName: analysis.sector_name,
                                  uploadedAt: analysis.uploaded_at
                                });
                              } catch (error) {
                                console.error('❌ Erreur génération URL signée:', error);
                                // Si erreur, ne pas afficher l'image
                                setSelectedHistoryAnalysis({
                                  analysis: analysisData,
                                  imageUrl: null,
                                  sectorName: analysis.sector_name,
                                  uploadedAt: analysis.uploaded_at
                                });
                              }
                            }}
                          >
                            <CardContent>
                              <Typography variant="h6" gutterBottom>
                                {analysis.sector_name || `Secteur ${analysis.sector_id}`}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                {new Date(analysis.uploaded_at).toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                  label={`Score: ${analysis.health_score || 0}/100`}
                                  color={
                                    (analysis.health_score >= 80) ? 'success' :
                                    (analysis.health_score >= 60) ? 'warning' : 'error'
                                  }
                                  size="small"
                                />
                                <Chip
                                  label={getAnalysisTypeLabel(analysis.ai_model)}
                                  size="small"
                                  variant="outlined"
                                  color={analysis.ai_model?.toLowerCase().includes('pro') ? 'secondary' : 'primary'}
                                />
                                {(() => {
                                  // Synthèse courte: maladies, nuisibles, anomalies
                                  const safeJsonParse = (v, d) => {
                                    if (typeof v === 'object' && v !== null) return v;
                                    if (!v || v === 'null' || (typeof v === 'string' && v.trim() === '')) return d;
                                    try { return JSON.parse(v); } catch { return d; }
                                  };
                                  const diseases = safeJsonParse(analysis.diseases_detected, []);
                                  const pests = safeJsonParse(analysis.pests_detected, []);
                                  const anomalies = safeJsonParse(analysis.growth_anomalies, []);
                                  const tags = [];
                                  if (Array.isArray(diseases) && diseases.length) tags.push(diseases[0]?.name);
                                  if (Array.isArray(pests) && pests.length) tags.push(pests[0]?.name);
                                  if (Array.isArray(anomalies) && anomalies.length) tags.push(anomalies[0]?.type || anomalies[0]?.name);
                                  return tags.filter(Boolean).slice(0,3).map((t, i) => (
                                    <Chip key={`t-${i}`} label={t} size="small" variant="outlined" color="warning" />
                                  ));
                                })()}
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

            </Box>
          </Paper>
        )}

        {/* No farm selected */}
        {!loadingFarms && !loadingFarmDetails && !selectedFarm && (
          <Alert severity="warning">
            Veuillez sélectionner une ferme pour commencer.
          </Alert>
        )}
      </Container>
    </Box>
  );
};

export default FarmRoadImageAnalysis;
